import React, { useMemo, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckIcon, InfoIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { Product, ProductOption, CartItemType } from '../types/types';
import { BOWL_OPTIONS, INGREDIENT_IMAGES, MENU_PRODUCTS } from '../data/menuData';
import { CONFIG } from '../data/config';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
interface ProductCustomizerProps {
  product: Product;
  onClose: () => void;
  onSave: (item: CartItemType) => void;
  initialItem?: CartItemType;
}
export function ProductCustomizer({
  product,
  onClose,
  onSave,
  initialItem
}: ProductCustomizerProps) {
  const isSandwichOrWrap = product.type === 'sandwich' || product.type === 'wrap';
  const isSnackBowl = product.type === 'snack_bowl';
  const {
    addItem
  } = useCart();
  const {
    t,
    formatPrice,
    translateCategory,
    translateSubcategory,
    translateProductName,
    translateProductDescription,
    translateIngredient
  } = useLanguage();
  const translatedName = translateProductName(product.id, product.name);
  const translatedDescription = translateProductDescription(product.id, product.description);
  const [selections, setSelections] = useState<any>(initialItem?.selections || {
    toppings: [],
    aderezos: [],
    comentarios: '',
    selectedSize: product.type === 'pizza' && product.sizes && product.sizes.length > 0 ? product.sizes[0] : undefined
  });
  // Half and Half state
  const [isHalfAndHalf, setIsHalfAndHalf] = useState(initialItem?.selections?.halfAndHalf?.enabled || false);
  const [secondHalf, setSecondHalf] = useState<Product | null>(() => {
    if (initialItem?.selections?.halfAndHalf?.secondHalfId) {
      return MENU_PRODUCTS.find(p => p.id === initialItem.selections.halfAndHalf?.secondHalfId) || null;
    }
    return null;
  });
  // Beverage state
  const [beverageOpen, setBeverageOpen] = useState(false);
  const [activeSubcategory, setActiveSubcategory] = useState<string | null>(null);
  const [selectedBeverage, setSelectedBeverage] = useState<Product | null>(null);
  // Group beverages by subcategory
  const beverageGroups = useMemo(() => {
    const groups: Record<string, Product[]> = {};
    MENU_PRODUCTS.filter(p => p.category === 'Bebidas').forEach(p => {
      const sub = p.subcategory || 'Otras';
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(p);
    });
    return groups;
  }, []);
  const handleToppingSelect = (option: ProductOption) => {
    const currentList = selections.toppings || [];
    const exists = currentList.find((item: ProductOption) => item.id === option.id);
    if (exists) {
      setSelections({
        ...selections,
        toppings: currentList.filter((item: ProductOption) => item.id !== option.id)
      });
    } else {
      if (isSandwichOrWrap && currentList.length >= 4) return;
      setSelections({
        ...selections,
        toppings: [...currentList, option]
      });
    }
  };
  const handleAderezoSelect = (option: ProductOption) => {
    const currentList = selections.aderezos || [];
    const exists = currentList.find((item: ProductOption) => item.id === option.id);
    if (exists) {
      setSelections({
        ...selections,
        aderezos: currentList.filter((item: ProductOption) => item.id !== option.id)
      });
    } else {
      if (isSandwichOrWrap && currentList.length >= 1) return;
      setSelections({
        ...selections,
        aderezos: [...currentList, option]
      });
    }
  };
  const calculateSubtotal = () => {
    let total = product.price;
    if (selections.selectedSize) {
      total = selections.selectedSize.price;
      // If half and half, use the price of the more expensive half
      if (isHalfAndHalf && secondHalf) {
        const secondHalfSize = secondHalf.sizes?.find(s => s.name === selections.selectedSize?.name);
        if (secondHalfSize && secondHalfSize.price > total) {
          total = secondHalfSize.price;
        }
      }
    }
    if (selectedBeverage) total += selectedBeverage.price;
    return total;
  };
  const handleSave = () => {
    const finalSelections = {
      ...selections
    };
    if (isHalfAndHalf && secondHalf) {
      finalSelections.halfAndHalf = {
        enabled: true,
        secondHalfId: secondHalf.id,
        secondHalfName: secondHalf.name
      };
    } else {
      delete finalSelections.halfAndHalf;
    }
    const item: CartItemType = {
      id: initialItem?.id || `${product.id}_${Date.now()}`,
      productId: product.id,
      name: product.name,
      basePrice: product.price,
      quantity: initialItem?.quantity || 1,
      selections: finalSelections,
      subtotal: calculateSubtotal(),
      type: product.type || 'bowl'
    };
    // Add beverage as separate cart item
    if (selectedBeverage) {
      addItem({
        id: `${selectedBeverage.id}_${Date.now()}`,
        productId: selectedBeverage.id,
        name: selectedBeverage.name,
        basePrice: selectedBeverage.price,
        quantity: 1,
        selections: {},
        subtotal: selectedBeverage.price,
        type: 'bebida'
      });
    }
    onSave(item);
  };
  const isSaveValid = () => {
    if (isSandwichOrWrap) {
      return (selections.toppings?.length || 0) >= 1;
    }
    if (product.type === 'pizza' && isHalfAndHalf && !secondHalf) {
      return false;
    }
    return true;
  };
  const toppingCount = selections.toppings?.length || 0;
  const aderezoCount = selections.aderezos?.length || 0;
  // Get all selected ingredients for visual progress
  const getAllSelectedIngredients = () => {
    const all: ProductOption[] = [];
    if (selections.toppings) all.push(...selections.toppings);
    if (selections.aderezos) all.push(...selections.aderezos);
    return all;
  };
  const selectedIngredients = getAllSelectedIngredients();
  const subtotalFormatted = formatPrice(calculateSubtotal());
  return <motion.div initial={{
    opacity: 0,
    y: 50
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 50
  }} className="fixed inset-0 z-50 bg-carbon flex flex-col max-w-md mx-auto shadow-2xl" data-id="element-1536">
      <div className="bg-surface px-4 py-4 flex items-center justify-between shadow-sm z-20 sticky top-0 border-b border-gray-800" data-id="element-1537">
        <button onClick={onClose} className="p-2 -ml-2 text-gray-400 hover:text-white bg-surface/80 rounded-full backdrop-blur-sm" data-id="element-1538">
          <XIcon className="w-6 h-6" data-id="element-1539" />
        </button>
        <h2 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1540">
          {t('personalize')}
        </h2>
        <div className="w-10" data-id="element-1541"></div>
      </div>

      <div className="flex-1 overflow-y-auto pb-24" data-id="element-1542">
        {/* Hero Image */}
        {product.image && <div className="w-full h-48 relative bg-surface-light" data-id="element-1543">
            <img src={product.image} alt={translatedName} className="w-full h-full object-cover" loading="eager" data-id="element-1544" />
            <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/40 to-transparent" data-id="element-1545" />
            <div className="absolute bottom-4 left-4 right-4 text-white" data-id="element-1546">
              <h3 className="text-3xl font-heading font-bold mb-1 drop-shadow-md tracking-wide" data-id="element-1547">
                {translatedName}
              </h3>
              <p className="text-gray-300 text-sm drop-shadow" data-id="element-1548">
                {translatedDescription}
              </p>
            </div>
          </div>}

        {/* Visual Progress Strip */}
        {isSandwichOrWrap && <div className="bg-surface/80 backdrop-blur-md border-b border-gray-800 py-3 px-4 z-10 shadow-sm sticky top-0" data-id="element-1549">
            <div className="flex items-center gap-3 overflow-x-auto hide-scrollbar pb-1 min-h-[60px]" data-id="element-1550">
              {selectedIngredients.length === 0 ? <p className="text-sm text-gray-500 italic w-full text-center" data-id="element-1551">
                  {t('yourIngredients')}
                </p> : <AnimatePresence data-id="element-1552">
                  {selectedIngredients.map(ing => <motion.div key={ing.id} initial={{
              opacity: 0,
              scale: 0.5
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.5
            }} className="flex flex-col items-center gap-1 flex-shrink-0" data-id="element-1553">
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-verde shadow-sm bg-surface-light" data-id="element-1554">
                        {INGREDIENT_IMAGES[ing.id] ? <img src={INGREDIENT_IMAGES[ing.id]} alt={translateIngredient(ing.id, ing.name)} className="w-full h-full object-cover" loading="lazy" data-id="element-1555" /> : <div className="w-full h-full bg-verde/10 flex items-center justify-center text-verde font-bold text-xs" data-id="element-1556">
                            {translateIngredient(ing.id, ing.name).charAt(0)}
                          </div>}
                      </div>
                      <span className="text-[10px] text-gray-400 font-medium max-w-[56px] truncate text-center" data-id="element-1557">
                        {translateIngredient(ing.id, ing.name)}
                      </span>
                    </motion.div>)}
                </AnimatePresence>}
            </div>
          </div>}

        <div className="p-4" data-id="element-1558">
          {!product.image && <div className="mb-6" data-id="element-1559">
              <h3 className="text-2xl font-heading font-bold text-white mb-1 tracking-wide" data-id="element-1560">
                {translatedName}
              </h3>
              <p className="text-gray-400 text-sm" data-id="element-1561">{translatedDescription}</p>
            </div>}

          <p className="text-verde font-semibold text-lg mb-6 flex items-baseline gap-2" data-id="element-1562">
            {selections.selectedSize ? formatPrice(selections.selectedSize.price).primary : formatPrice(product.price).primary}
            <span className="text-sm text-gray-400 font-normal" data-id="element-1563">
              {selections.selectedSize ? formatPrice(selections.selectedSize.price).secondary : formatPrice(product.price).secondary}
            </span>
          </p>

          {product.type === 'pizza' && product.sizes && product.sizes.length > 0 && <div className="mb-8" data-id="element-1564">
                <h4 className="font-heading font-semibold text-lg text-white tracking-wide mb-3" data-id="element-1565">
                  Tamaño
                </h4>
                <div className="space-y-2" data-id="element-1566">
                  {product.sizes.map(size => <button key={size.name} onClick={() => {
              setSelections({
                ...selections,
                selectedSize: size
              });
              // If half and half is active, check if the second half has this size. If not, clear it.
              if (isHalfAndHalf && secondHalf) {
                const hasSize = secondHalf.sizes?.some(s => s.name === size.name);
                if (!hasSize) {
                  setSecondHalf(null);
                }
              }
            }} className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${selections.selectedSize?.name === size.name ? 'border-verde bg-verde/10 ring-1 ring-verde' : 'border-gray-700 bg-surface hover:border-gray-500'}`} data-id="element-1567">
                      <div className="text-left" data-id="element-1568">
                        <p className="font-medium text-white" data-id="element-1569">{size.name}</p>
                        {size.slices && <p className="text-xs text-gray-400" data-id="element-1570">{size.slices}</p>}
                      </div>
                      <p className="font-semibold text-verde" data-id="element-1571">
                        {formatPrice(size.price).primary}
                      </p>
                    </button>)}
                </div>
              </div>}

          {product.type === 'pizza' && product.sizes && product.sizes.length > 0 && <div className="mb-8" data-id="element-1572">
                <div className="flex items-center justify-between mb-3" data-id="element-1573">
                  <div data-id="element-1574">
                    <h4 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1575">
                      Mitad y Mitad
                    </h4>
                    <p className="text-xs text-gray-400" data-id="element-1576">
                      Combina dos sabores en una sola pizza
                    </p>
                  </div>
                  <button onClick={() => {
              setIsHalfAndHalf(!isHalfAndHalf);
              if (isHalfAndHalf) setSecondHalf(null);
            }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isHalfAndHalf ? 'bg-verde' : 'bg-gray-700'}`} data-id="element-1577">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isHalfAndHalf ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1578" />
                  </button>
                </div>

                <AnimatePresence data-id="element-1579">
                  {isHalfAndHalf && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-1580">
                      <div className="pt-2" data-id="element-1581">
                        <p className="text-sm text-gray-300 mb-3" data-id="element-1582">
                          Selecciona la segunda mitad (se aplicará el precio de
                          la mitad mayor):
                        </p>
                        <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar" data-id="element-1583">
                          {MENU_PRODUCTS.filter(p => p.type === 'pizza' && p.id !== product.id && p.sizes?.some(s => s.name === selections.selectedSize?.name)).map(pizza => <button key={pizza.id} onClick={() => setSecondHalf(pizza)} className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between h-full ${secondHalf?.id === pizza.id ? 'border-verde bg-verde/10 ring-1 ring-verde' : 'border-gray-700 bg-surface hover:border-gray-500'}`} data-id="element-1584">
                              <span className="font-medium text-sm text-white leading-tight mb-1" data-id="element-1585">
                                {translateProductName(pizza.id, pizza.name)}
                              </span>
                              <span className="text-xs text-verde font-semibold" data-id="element-1586">
                                {formatPrice(pizza.sizes?.find(s => s.name === selections.selectedSize?.name)?.price || 0).primary}
                              </span>
                            </button>)}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>}

          {isSandwichOrWrap && <>
              {/* Toppings Section */}
              <div className="mb-8" data-id="element-1587">
                <div className="flex justify-between items-end mb-1" data-id="element-1588">
                  <h4 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1589">
                    {t('toppings')}
                  </h4>
                  <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${toppingCount >= 1 ? 'bg-verde/10 text-verde' : 'bg-surface-light text-gray-400'}`} data-id="element-1590">
                    {toppingCount}/4
                  </span>
                </div>
                <p className="text-gray-500 text-xs mb-3" data-id="element-1591">
                  {t('chooseToppings')}
                </p>

                {toppingCount === 4 && <p className="text-xs text-verde bg-verde/10 px-3 py-1.5 rounded-lg mb-3 flex items-center gap-1 border border-verde/20" data-id="element-1592">
                    <CheckIcon className="w-3 h-3" data-id="element-1593" /> {t('maxReached')}
                  </p>}

                <div className="grid grid-cols-2 gap-2" data-id="element-1594">
                  {BOWL_OPTIONS.toppings.map(option => {
                const isSelected = selections.toppings?.some((t: ProductOption) => t.id === option.id);
                const isDisabled = !isSelected && toppingCount >= 4;
                return <button key={option.id} onClick={() => handleToppingSelect(option)} disabled={isDisabled} className={`p-2 rounded-xl border text-sm text-left transition-colors flex items-center gap-2 ${isSelected ? 'border-verde bg-verde/10 text-verde font-medium' : isDisabled ? 'border-gray-800 bg-surface-light text-gray-600 cursor-not-allowed' : 'border-gray-700 bg-surface text-gray-300 hover:border-gray-500'}`} data-id="element-1595">
                        {INGREDIENT_IMAGES[option.id] && <img src={INGREDIENT_IMAGES[option.id]} alt="" className={`w-8 h-8 rounded-full object-cover flex-shrink-0 ${isSelected ? 'ring-2 ring-verde ring-offset-1 ring-offset-surface' : ''}`} loading="lazy" data-id="element-1596" />}
                        <span className="leading-tight flex-1" data-id="element-1597">
                          {translateIngredient(option.id, option.name)}
                        </span>
                      </button>;
              })}
                </div>
              </div>

              {/* Aderezos Section */}
              <div className="mb-8" data-id="element-1598">
                <div className="flex justify-between items-end mb-1" data-id="element-1599">
                  <div className="flex items-center gap-2" data-id="element-1600">
                    <h4 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1601">
                      {t('dressing')}
                    </h4>
                    <span className="text-xs font-medium bg-surface-light text-gray-400 px-2 py-0.5 rounded-full border border-gray-700" data-id="element-1602">
                      {t('optional')}
                    </span>
                  </div>
                  <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${aderezoCount >= 1 ? 'bg-verde/10 text-verde' : 'bg-surface-light text-gray-400'}`} data-id="element-1603">
                    {aderezoCount}/1
                  </span>
                </div>
                <p className="text-gray-500 text-xs mb-3" data-id="element-1604">
                  {t('chooseDressing')}
                </p>

                <div className="grid grid-cols-2 gap-2" data-id="element-1605">
                  {BOWL_OPTIONS.aderezos.map(option => {
                const isSelected = selections.aderezos?.some((a: ProductOption) => a.id === option.id);
                const isDisabled = !isSelected && aderezoCount >= 1;
                return <button key={option.id} onClick={() => handleAderezoSelect(option)} disabled={isDisabled} className={`p-2 rounded-xl border text-sm text-left transition-colors flex items-center gap-2 ${isSelected ? 'border-verde bg-verde/10 text-verde font-medium' : isDisabled ? 'border-gray-800 bg-surface-light text-gray-600 cursor-not-allowed' : 'border-gray-700 bg-surface text-gray-300 hover:border-gray-500'}`} data-id="element-1606">
                        {INGREDIENT_IMAGES[option.id] && <img src={INGREDIENT_IMAGES[option.id]} alt="" className={`w-8 h-8 rounded-full object-cover flex-shrink-0 ${isSelected ? 'ring-2 ring-verde ring-offset-1 ring-offset-surface' : ''}`} loading="lazy" data-id="element-1607" />}
                        <span className="leading-tight flex-1" data-id="element-1608">
                          {translateIngredient(option.id, option.name)}
                        </span>
                      </button>;
              })}
                </div>
              </div>

              {/* Beverage Accordion */}
              <div className="mb-8" data-id="element-1609">
                <button onClick={() => {
              setBeverageOpen(!beverageOpen);
              if (beverageOpen) setActiveSubcategory(null);
            }} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${selectedBeverage ? 'border-verde bg-verde/10' : beverageOpen ? 'border-verde/50 bg-verde/5' : 'border-gray-700 bg-surface'}`} data-id="element-1610">
                  <div className="flex items-center gap-3" data-id="element-1611">
                    <span className="text-xl" data-id="element-1612">🥤</span>
                    <div className="text-left" data-id="element-1613">
                      <h4 className="font-heading font-semibold text-base text-white tracking-wide" data-id="element-1614">
                        {t('beverages')}
                      </h4>
                      {selectedBeverage ? <p className="text-xs text-verde font-medium" data-id="element-1615">
                          {translateProductName(selectedBeverage.id, selectedBeverage.name)}{' '}
                          · +{formatPrice(selectedBeverage.price).primary}
                        </p> : <p className="text-xs text-gray-400" data-id="element-1616">
                          {t('beverageOptional')}
                        </p>}
                    </div>
                  </div>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${beverageOpen ? 'rotate-180' : ''}`} data-id="element-1617" />
                </button>

                <AnimatePresence data-id="element-1618">
                  {beverageOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-1619">
                      <div className="pt-3 space-y-2" data-id="element-1620">
                        {/* Subcategory buttons */}
                        {!activeSubcategory && <div className="grid grid-cols-2 gap-2" data-id="element-1621">
                            {Object.entries(beverageGroups).map(([subcat, products]) => <button key={subcat} onClick={() => setActiveSubcategory(subcat)} className="p-3 rounded-xl border border-gray-700 bg-surface text-left hover:border-gray-500 transition-colors flex items-center justify-between" data-id="element-1622">
                                  <div data-id="element-1623">
                                    <p className="text-sm font-medium text-gray-200" data-id="element-1624">
                                      {translateSubcategory(subcat)}
                                    </p>
                                    <p className="text-[11px] text-gray-500" data-id="element-1625">
                                      {products.length} {t('options')}
                                    </p>
                                  </div>
                                  <ChevronRightIcon className="w-4 h-4 text-gray-500" data-id="element-1626" />
                                </button>)}
                          </div>}

                        {/* Specific beverages within subcategory */}
                        {activeSubcategory && <div data-id="element-1627">
                            <button onClick={() => setActiveSubcategory(null)} className="text-sm text-verde font-medium mb-3 flex items-center gap-1" data-id="element-1628">
                              ← {translateSubcategory(activeSubcategory)}
                            </button>
                            <div className="grid grid-cols-2 gap-2" data-id="element-1629">
                              {beverageGroups[activeSubcategory]?.map(bev => {
                        const isSelected = selectedBeverage?.id === bev.id;
                        return <button key={bev.id} onClick={() => {
                          setSelectedBeverage(isSelected ? null : bev);
                          if (!isSelected) {
                            setActiveSubcategory(null);
                            setBeverageOpen(false);
                          }
                        }} className={`rounded-xl border overflow-hidden text-left transition-all ${isSelected ? 'border-verde ring-2 ring-verde/20 bg-verde/5' : 'border-gray-700 bg-surface hover:border-gray-500'}`} data-id="element-1630">
                                    {bev.image && <div className="h-20 w-full bg-surface-light relative" data-id="element-1631">
                                        <img src={bev.image} alt={translateProductName(bev.id, bev.name)} loading="lazy" className="w-full h-full object-cover" data-id="element-1632" />
                                        {isSelected && <div className="absolute top-1.5 right-1.5 bg-verde text-carbon rounded-full p-0.5" data-id="element-1633">
                                            <CheckIcon className="w-3 h-3" data-id="element-1634" />
                                          </div>}
                                      </div>}
                                    <div className="p-2" data-id="element-1635">
                                      <p className="text-[11px] font-medium text-gray-200 leading-tight line-clamp-2 min-h-[28px]" data-id="element-1636">
                                        {translateProductName(bev.id, bev.name)}
                                      </p>
                                      <p className={`text-[11px] font-semibold mt-1 ${isSelected ? 'text-verde' : 'text-gray-400'}`} data-id="element-1637">
                                        +{formatPrice(bev.price).primary}
                                      </p>
                                    </div>
                                  </button>;
                      })}
                            </div>
                          </div>}

                        {/* Selected beverage confirmation */}
                        {selectedBeverage && !activeSubcategory && <div className="flex items-center justify-between bg-verde/10 px-3 py-2.5 rounded-xl border border-verde/20" data-id="element-1638">
                            <div className="flex items-center gap-2" data-id="element-1639">
                              {selectedBeverage.image && <img src={selectedBeverage.image} alt="" className="w-8 h-8 rounded-lg object-cover" data-id="element-1640" />}
                              <div data-id="element-1641">
                                <p className="text-sm text-verde font-medium" data-id="element-1642">
                                  {translateProductName(selectedBeverage.id, selectedBeverage.name)}
                                </p>
                                <p className="text-[11px] text-verde/80" data-id="element-1643">
                                  +{formatPrice(selectedBeverage.price).primary}
                                </p>
                              </div>
                            </div>
                            <button onClick={() => setSelectedBeverage(null)} className="text-xs text-gray-300 bg-surface px-2.5 py-1 rounded-lg border border-gray-700 hover:text-white" data-id="element-1644">
                              {t('remove')}
                            </button>
                          </div>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </>}

          {/* Comments Section */}
          <div className="mb-8" data-id="element-1645">
            <div className="flex items-center gap-2 mb-1" data-id="element-1646">
              <h4 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1647">
                {t('comments')}
              </h4>
              <span className="text-xs font-medium bg-surface-light text-gray-400 px-2 py-0.5 rounded-full border border-gray-700" data-id="element-1648">
                {t('optional')}
              </span>
            </div>
            <p className="text-gray-500 text-xs mb-3" data-id="element-1649">
              {t('specialInstructions')}
            </p>
            <textarea value={selections.comentarios} onChange={e => setSelections({
            ...selections,
            comentarios: e.target.value
          })} placeholder={t('commentsPlaceholder')} className="w-full h-24 p-4 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none text-white placeholder-gray-500" data-id="element-1650" />
          </div>

          {/* Visual Preview — Stacked Ingredient Composition */}
          {isSandwichOrWrap && selectedIngredients.length > 0 && <div className="mb-6" data-id="element-1651">
              <h4 className="font-heading font-semibold text-lg mb-3 text-white tracking-wide" data-id="element-1652">
                {t('yourBowl').replace('Bowl', translatedName)}
              </h4>
              <div className="rounded-2xl overflow-hidden border border-gray-800 shadow-sm bg-surface-light relative" data-id="element-1653">
                {/* Stacked ingredients — layered composition */}
                <div className="relative w-full h-56 flex items-center justify-center" data-id="element-1654">
                  {/* Base bread/tortilla layer */}
                  {product.image && <div className="absolute inset-0 opacity-20" data-id="element-1655">
                      <img src={product.image} alt="" className="w-full h-full object-cover blur-sm" data-id="element-1656" />
                    </div>}
                  {/* Ingredient stack */}
                  <div className="relative w-52 h-52" data-id="element-1657">
                    <AnimatePresence data-id="element-1658">
                      {selectedIngredients.map((ing, idx) => {
                    const total = selectedIngredients.length;
                    const angle = idx / total * 360;
                    const radius = total <= 3 ? 40 : 55;
                    const x = Math.cos(angle * Math.PI / 180) * radius;
                    const y = Math.sin(angle * Math.PI / 180) * radius;
                    const size = total <= 2 ? 88 : total <= 4 ? 76 : 64;
                    const rotation = idx * 15 - 20;
                    return <motion.div key={ing.id} initial={{
                      opacity: 0,
                      scale: 0,
                      y: 30
                    }} animate={{
                      opacity: 1,
                      scale: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      scale: 0
                    }} transition={{
                      delay: idx * 0.08,
                      type: 'spring',
                      damping: 15
                    }} className="absolute rounded-full overflow-hidden shadow-xl border-2 border-gray-700" style={{
                      width: size,
                      height: size,
                      left: `calc(50% + ${x}px - ${size / 2}px)`,
                      top: `calc(50% + ${y}px - ${size / 2}px)`,
                      zIndex: idx + 1,
                      transform: `rotate(${rotation}deg)`
                    }} data-id="element-1659">
                            {INGREDIENT_IMAGES[ing.id] ? <img src={INGREDIENT_IMAGES[ing.id]} alt={translateIngredient(ing.id, ing.name)} className="w-full h-full object-cover" data-id="element-1660" /> : <div className="w-full h-full bg-surface flex items-center justify-center text-white font-bold" data-id="element-1661">
                                {translateIngredient(ing.id, ing.name).charAt(0)}
                              </div>}
                          </motion.div>;
                  })}
                    </AnimatePresence>
                  </div>
                </div>
                {/* Ingredient labels */}
                <div className="bg-surface px-4 py-3 border-t border-gray-800" data-id="element-1662">
                  <div className="flex flex-wrap gap-1.5 justify-center" data-id="element-1663">
                    {selectedIngredients.map(ing => <span key={ing.id} className="text-[11px] bg-surface-light text-gray-300 px-2.5 py-1 rounded-full font-medium border border-gray-700" data-id="element-1664">
                        {translateIngredient(ing.id, ing.name)}
                      </span>)}
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2" data-id="element-1665">
                    {toppingCount} {t('toppings').toLowerCase()}
                    {aderezoCount > 0 && ` · ${translateIngredient(selections.aderezos[0].id, selections.aderezos[0].name)}`}
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </div>

      <div className="bg-surface border-t border-gray-800 p-4 pb-safe sticky bottom-0 z-20" data-id="element-1666">
        {!isSaveValid() && isSandwichOrWrap && <p className="text-center text-xs text-terracota mb-2 flex items-center justify-center gap-1" data-id="element-1667">
            <InfoIcon className="w-3 h-3" data-id="element-1668" />
            {t('chooseMin1')}
          </p>}
        <button onClick={handleSave} disabled={!isSaveValid()} className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors min-h-[48px] border border-white/10 ${isSaveValid() ? 'bg-terracota text-white active:bg-[#9a3a18] shadow-lg shadow-terracota/20' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`} data-id="element-1669">
          {t('addToOrder')} — {subtotalFormatted.primary}
        </button>
      </div>
    </motion.div>;
}